import { Component, inject } from '@angular/core';
import { Firestore, doc, docData, collection, collectionData, query, orderBy, Timestamp } from '@angular/fire/firestore';
import { of } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

// Content alignment types
type ContentAlignment = 'left' | 'center' | 'right';

// Content info structures matching iOS/Android
interface ContentInfoTextType {
  alignment: {
    [key in ContentAlignment]?: {}
  }
}

interface ContentInfoType {
  text: ContentInfoTextType;
}

interface ContentInfo {
  type: ContentInfoType;
}

// Main data models
interface Body {
  id: string;
  text: string;
  content_info?: ContentInfo;
  created_at?: Timestamp;
  created_by?: User;
}

interface Story {
  id: string;
  text: string;
  content_info?: ContentInfo;
  links?: Link[];
  created_at?: Timestamp;
  created_by?: User;
  is_deleted?: boolean;
}

interface Link {
  id: string;
  url: string;
  story_id: string;
  created_at?: Timestamp;
}

interface User {
  uid?: string;
  email?: string;
  created_at?: Timestamp;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  link$: Observable<Link> | null = null;
  story$: Observable<Story> | null = null;
  bodies$: Observable<Body[]> | null = null;
  storyContent$: Observable<{text: SafeHtml, alignment: string}> | null = null;
  bodiesContent$: Observable<{text: SafeHtml, alignment: string}[]> | null = null;
  private firestore: Firestore = inject(Firestore);

  constructor(private sanitizer: DomSanitizer, private router: Router) {
    this.router.events
    .pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => {
        const segments = this.router.url.split('/').filter(segment => segment);
        return segments.length > 0 ? segments[0] : null;
      }),
      filter((id) => !!id)
    )
    .subscribe((id) => {
      // getting link
      const linkRef = doc(this.firestore, `links/${id}`);
      this.link$ = docData(linkRef, { idField: 'id' });

      this.story$ = this.link$!.pipe(
        switchMap((link: any) => {
          if (!link || !link.story_id) {
            return [];
          }
          const storyRef = doc(this.firestore, `stories/${link.story_id}`);
          return docData(storyRef, { idField: 'id' });
        })
      );

      this.bodies$ = this.story$.pipe(
        switchMap((story: any) => {
          if (!story) {
            return of([]);
          }
          const bodiesRef = collection(this.firestore, `stories/${story.id}/bodies`);
          const bodiesQuery = query(bodiesRef, orderBy('created_at', 'asc'));
          return collectionData(bodiesQuery, { idField: 'id' });
        })
      );

      this.storyContent$ = this.story$.pipe(
        map((story: Story) => {
          if (!story || !story.text) {
            return { text: this.sanitizer.bypassSecurityTrustHtml(''), alignment: 'left' };
          }
          return {
            text: this.toPlainHTML(story.text),
            alignment: this.getAlignment(story.content_info)
          };
        })
      );

      this.bodiesContent$ = this.bodies$.pipe(
        map((bodies: Body[]) => {
          if (!bodies || bodies.length === 0) {
            return [];
          }
          return bodies.map((body: Body) => ({
            text: this.toPlainHTML(body.text),
            alignment: this.getAlignment(body.content_info)
          }));
        })
      );
    });
  }

  /**
   * Convert plain text to HTML with line breaks
   * Handles newlines and escapes HTML to prevent XSS
   */
  private toPlainHTML(text: string): SafeHtml {
    if (!text) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    // Escape HTML to prevent XSS, then replace newlines with <br>
    const escapedText = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
      .replace(/\n/g, '<br>');

    return this.sanitizer.bypassSecurityTrustHtml(escapedText);
  }

  /**
   * Extract text alignment from content_info
   * Returns 'left', 'center', or 'right'
   */
  private getAlignment(contentInfo?: ContentInfo): string {
    if (!contentInfo?.type?.text?.alignment) {
      return 'left';
    }

    const alignmentMap = contentInfo.type.text.alignment;

    if (alignmentMap.center !== undefined) {
      return 'center';
    } else if (alignmentMap.right !== undefined) {
      return 'right';
    }

    return 'left';
  }
}